import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApolloError, useApolloClient } from "@apollo/client";
import { FormattedMessage, useIntl } from "react-intl";

import { PageName } from "analytics/events";
import { ExternalProviderOrderNotice } from "flows/Picking/components/ExternalProviderOrderNotice";
import { NewOrderDetails } from "flows/Picking/components/NewOrderDetails/NewOrderDetails";
import {
  PickingConflictData,
  PickingConflictModal,
} from "flows/Picking/components/PickingConflictModal";
import { StartPickingRefusalModal } from "flows/Picking/components/StartPickingRefusalModal";
import { OrderDetails } from "flows/Picking/models/order/types";
import { getExternalProvider } from "flows/Picking/utils/externalProviderOrder";
import { extractConflictData, isPickingConflictError } from "flows/Picking/utils/pickingConflict";
import {
  getStartPickingRefusalCode,
  StartPickingRefusalCode,
} from "flows/Picking/utils/startPickingRefusal";
import { Page } from "shared/components/Page";
import { ScannableInput } from "shared/components/ScannableInput";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useErrorToastWithAudio } from "shared/hooks/useErrorToastWithAudio";
import { Button } from "ui/Button/Button";
import { Form } from "ui/Form/Form";
import { HeaderS } from "ui/Typography/Typography";
import { getErrorMessage } from "utils/error";
import { getOrderNumberFromQRCode } from "utils/scan";

import { usePickingActions } from "../hooks/usePickingActions";
import {
  serializeOrderDetails,
  serializeStartPickingOrderResponse,
} from "../models/order/serializer";
import {
  GetOrderDetailsDocument,
  GetOrderDetailsQuery,
  GetOrderDetailsQueryVariables,
  useStartPickingOrderMutation,
} from "../queries/order/order.generated";
import { usePickingStore } from "../stores/usePickingStore";

export const isValidOrderNumber = (orderNumber: string) => {
  const trimmed = orderNumber.trim();
  const orderNumberPattern = /^[a-zA-Z]{2}-[a-zA-Z]{3}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}$/;

  return trimmed !== "" && orderNumberPattern.test(trimmed);
};
export function ManualPickingPage() {
  const intl = useIntl();
  const client = useApolloClient();
  const { showToastAndPlayError } = useErrorToastWithAudio({
    errorToastId: "start-picking-error",
  });

  const { startPicking: startPickingAction, goToStuck, finishPicking } = usePickingActions();

  const [orderNumberValue, setOrderNumberValue] = useState<string>("");
  const [isInvalidInput, setIsInvalidInput] = useState(false);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [externalProviderOrder, setExternalProviderOrder] = useState<{
    externalProvider: string;
    orderNumber: string;
  } | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const { sendSegmentTrackEvent } = useAnalytics();
  const [startPickingOrderMutation, { loading: loadingStartPickingOrder }] =
    useStartPickingOrderMutation();

  const [conflictData, setConflictData] = useState<PickingConflictData | null>(null);
  const [refusalCode, setRefusalCode] = useState<StartPickingRefusalCode | null>(null);

  const handleChangeInputValue = (value: string) => {
    setIsInvalidInput(false);
    setOrderNumberValue(value);
  };

  // We have discovered a bug in the app that OAs sometimes press the back button, and orders are stuck in the picking state.
  // So, we are adding this check to redirect users to the stuck page if they are not in the correct state.
  // This only needs to run on mount — not on every re-render (e.g., feature flag changes).
  useEffect(() => {
    const { endPickingPhase, pickingPhase } = usePickingStore.getState();
    if (endPickingPhase !== "idle" || pickingPhase !== "startManualPicking") {
      goToStuck();
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrderDetails = useCallback(
    async (orderNumber: string) => {
      const orderNumberParsed = getOrderNumberFromQRCode(orderNumber);
      const isValid = isValidOrderNumber(orderNumberParsed);
      setIsInvalidInput(!isValid);
      if (!isValid) return;

      try {
        setIsLoadingDetails(true);
        const { data } = await client.query<GetOrderDetailsQuery, GetOrderDetailsQueryVariables>({
          query: GetOrderDetailsDocument,
          variables: { orderNumber: orderNumberParsed },
          fetchPolicy: "network-only",
        });
        const orderDetailsData = data?.getOrderDetails;
        if (orderDetailsData?.id) {
          setOrderDetails(serializeOrderDetails(orderDetailsData));
        }
      } catch (err) {
        const externalProvider = getExternalProvider(err);
        if (externalProvider !== null) {
          setExternalProviderOrder({ externalProvider, orderNumber: orderNumberParsed });
          sendSegmentTrackEvent("errorShown", {
            screen_name: PageName.MANUAL_PICKING_PAGE,
            component_value: "PICKING_ORDER_EXTERNAL_PROVIDER",
          });
          return;
        }
        const errorCode =
          err instanceof ApolloError ? err.graphQLErrors?.[0]?.extensions?.code : undefined;
        showToastAndPlayError({
          title:
            errorCode === "PICKING_ORDER_NOT_FOUND"
              ? "components.picking.new-order-summary.order-not-found-title"
              : "components.picking.new-order-summary.start-picking-generic-error-title",
        });
        sendSegmentTrackEvent("errorShown", {
          screen_name: PageName.MANUAL_PICKING_PAGE,
          component_value: getErrorMessage(err),
        });
      } finally {
        setIsLoadingDetails(false);
      }
    },
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client],
  );

  const handleScanOrSubmit = useCallback(
    (orderNumber: string) => {
      fetchOrderDetails(orderNumber);
    },
    [fetchOrderDetails],
  );

  const requestInProgress = useRef(false);

  const onStartPickingFromDetails = useCallback(async () => {
    if (loadingStartPickingOrder || requestInProgress.current) return;
    if (!orderDetails?.number) {
      showToastAndPlayError({
        title: "components.picking.new-order-summary.start-picking-generic-error-title",
      });
      setOrderDetails(null);
      return;
    }

    requestInProgress.current = true;
    try {
      const { data } = await startPickingOrderMutation({
        variables: { orderId: orderDetails.id, isAlwaysInbound: true, manualPicking: true },
      });
      const details = data?.startPickingOrder?.orderDetails;
      if (!details) {
        showToastAndPlayError({
          title: "components.picking.new-order-summary.start-picking-generic-error-title",
        });
        setOrderDetails(null);
        return;
      }

      const serializedOrder = serializeStartPickingOrderResponse(details);
      startPickingAction(serializedOrder, "QR_CODE");
    } catch (err) {
      if (isPickingConflictError(err)) {
        const conflict = extractConflictData(err as ApolloError);
        if (conflict) {
          setConflictData(conflict);
          return;
        }
      }
      const startPickingRefusalCode = getStartPickingRefusalCode(err);
      if (startPickingRefusalCode) {
        setRefusalCode(startPickingRefusalCode);
        sendSegmentTrackEvent("errorShown", {
          screen_name: PageName.MANUAL_PICKING_PAGE,
          component_value: startPickingRefusalCode,
        });
        return;
      }
      showToastAndPlayError({
        title: "components.picking.new-order-summary.start-picking-generic-error-title",
      });
      setOrderDetails(null);
      sendSegmentTrackEvent("errorShown", {
        screen_name: PageName.MANUAL_PICKING_PAGE,
        component_value: getErrorMessage(err),
      });
    } finally {
      requestInProgress.current = false;
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetails, loadingStartPickingOrder]);

  const onFormSubmit = useCallback(
    (e?: React.FormEvent<HTMLFormElement>) => {
      e?.preventDefault();
      handleScanOrSubmit(orderNumberValue);
    },
    [handleScanOrSubmit, orderNumberValue],
  );

  const isSubmitButtonDisabled = isInvalidInput || isLoadingDetails;
  const errorMessage = useMemo(() => {
    if (isInvalidInput) {
      return intl.formatMessage({ id: "pages.picking.manual-picking.invalid-order-format" });
    }
    return undefined;
  }, [isInvalidInput, intl]);

  if (externalProviderOrder) {
    return (
      <Page isBgGrey padding="s300">
        <ExternalProviderOrderNotice
          externalProvider={externalProviderOrder.externalProvider}
          orderNumber={externalProviderOrder.orderNumber}
          onAcknowledge={() => setExternalProviderOrder(null)}
        />
      </Page>
    );
  }

  if (orderDetails) {
    return (
      <Page isBgGrey padding="s300">
        <NewOrderDetails
          orderDetails={orderDetails}
          onStartPicking={onStartPickingFromDetails}
          onConfirmCancelled={finishPicking}
          isStartPickingLoading={loadingStartPickingOrder}
        />
        {conflictData && (
          <PickingConflictModal
            isOpen
            hideProceed
            conflictData={conflictData}
            onClose={() => {
              setConflictData(null);
              setOrderDetails(null);
            }}
            onProceed={() => {
              setConflictData(null);
              setOrderDetails(null);
            }}
          />
        )}
        {refusalCode && (
          <StartPickingRefusalModal
            isOpen
            refusalCode={refusalCode}
            onClose={() => {
              setRefusalCode(null);
              setOrderDetails(null);
            }}
          />
        )}
      </Page>
    );
  }

  return (
    <Page isCentered isLoading={isLoadingDetails}>
      <HeaderS textAlign="center">
        <FormattedMessage id="pages.picking.manual-picking.title" />
      </HeaderS>
      <Form w="100%" onSubmit={onFormSubmit}>
        <ScannableInput
          error={errorMessage}
          placeholder={intl.formatMessage({
            id: "pages.picking.manual-picking.input-placeholder",
          })}
          onScan={(scannedValue) => {
            handleScanOrSubmit(scannedValue);
          }}
          isAutoFocus
          onValueChange={handleChangeInputValue}
        />

        <Button
          isLoading={isLoadingDetails}
          isDisabled={isSubmitButtonDisabled}
          onClick={() => handleScanOrSubmit(orderNumberValue)}
          size="lg"
          variant="primary"
          data-testid="button-submit-button"
          isFullWidth
          mt="s300"
        >
          {intl.formatMessage({ id: "pages.picking.manual-picking.submit-button-label" })}
        </Button>
      </Form>
    </Page>
  );
}
